import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import { Box } from '@lifeomic/chroma-react/components/Box';

import { DashboardInjectedProps } from '../../../util/types';
import { ReportsTable } from './ReportsTable';
import { useLocalStorageState } from '../../../../../hooks';
import { TemplatesContent } from './TemplatesTable';
import { TemplatesProvider } from './Context';
import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import { Code } from '@lifeomic/chromicons';
import urls from '../../../../../util/urls';

export type AiReportsOwnProps = {
  defaultTab?: 'reports' | 'templates';
  selectedTabId?: 'reports' | 'templates';
  onTabChange?: (tab: 'reports' | 'templates') => void;
  selectedReportId?: string | null;
  onSelectedReportIdChange?: (id: string | null) => void;
};

const messages = defineMessages({
  reportsTab: {
    id: 'aiTools.tabs.reports',
    defaultMessage: 'Reports',
  },
  templatesTab: {
    id: 'aiTools.tabs.templates',
    defaultMessage: 'Templates',
  },
  aiToolsOptions: {
    id: 'aiTools.tabs.options',
    defaultMessage: 'AI Tools options',
  },
  closeEditingReport: {
    id: 'aiTools.closeEditingReport',
    defaultMessage: 'Back',
  },
  manageTemplates: {
    id: 'aiTools.manageTemplates',
    defaultMessage: 'Manage Templates',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  tab: {
    padding: 0,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.black[50],
    flex: 1,
    overflow: 'auto',
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  editingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
}));

export type AiReportsProps = AiReportsOwnProps & DashboardInjectedProps;

export const AiReports: React.FC<AiReportsProps> = ({
  defaultTab = 'reports',
  selectedTabId,
  onTabChange,
  selectedReportId,
  onSelectedReportIdChange,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  // keep track of the selected tab so it re-opens on navigation
  const [selectedTab, setSelectedTab] = useLocalStorageState(
    'ai-tools-selected-tab',
    defaultTab,
  );
  const activeTab = selectedTabId || selectedTab;
  const setActiveTab = (tab: 'reports' | 'templates') => {
    if (onTabChange) {
      onTabChange(tab);
      return;
    }
    setSelectedTab(tab);
  };

  return (
    <TemplatesProvider patientId={props.patient.id}>
      <div className={classes.root}>
        <div className={classes.tabsContainer}>
          <Tabs selectedTabId={activeTab} variant="pill">
            <Box margin={1} gap={1}>
              <TabList aria-label={formatMessage(messages.aiToolsOptions)}>
                <Tab stopId="reports" onClick={() => setActiveTab('reports')}>
                  {formatMessage(messages.reportsTab)}
                </Tab>
                <Tab
                  stopId="templates"
                  onClick={() => setActiveTab('templates')}
                >
                  {formatMessage(messages.templatesTab)}
                </Tab>
              </TabList>
              <ButtonLink
                icon={Code}
                to={urls.app.workflows.reportTemplates()}
                variant="text"
              >
                {formatMessage(messages.manageTemplates)}
              </ButtonLink>
            </Box>
            <TabPanel stopId="reports" className={classes.tab}>
              <ReportsTable
                patient={props.patient}
                selectedReportId={selectedReportId}
                onSelectedReportIdChange={onSelectedReportIdChange}
              />
            </TabPanel>
            <TabPanel stopId="templates" className={classes.tab}>
              <TemplatesContent />
            </TabPanel>
          </Tabs>
        </div>
      </div>
    </TemplatesProvider>
  );
};
